import { Check, Circle } from "lucide-react";

import { checkRequirements, scorePassword } from "./passwordStrength";

import styles from "./AuthLayout.module.css";

interface MeterProps {
  password: string;
  /** When false, hide the requirements checklist — useful for compact rows. */
  showRequirements?: boolean;
}

export function PasswordStrengthMeter({
  password,
  showRequirements = true,
}: MeterProps) {
  const score = scorePassword(password);
  const reqs = checkRequirements(password);

  return (
    <div>
      <div
        className={styles.strength}
        aria-hidden
        data-testid="strength-meter"
        data-level={score.level}
      >
        {[0, 1, 2, 3].map((i) => {
          const fill = i < score.filled ? styles[score.level] : "";
          return (
            <span key={i} className={`${styles.bar} ${fill}`.trim()} />
          );
        })}
      </div>
      <div className={styles.strengthLabel}>
        Strength: <span className={`${styles.lvl} ${styles[score.level]}`}>{score.label}</span>
      </div>
      {showRequirements && (
        <ul className={styles.reqs}>
          <ReqRow ok={reqs.longEnough}>At least 12 characters</ReqRow>
          <ReqRow ok={reqs.hasLetterAndDigit}>One letter and one digit</ReqRow>
          <ReqRow ok={reqs.notCommon}>Not a common password</ReqRow>
          <ReqRow ok={reqs.hasSymbol}>Optional: symbol</ReqRow>
        </ul>
      )}
    </div>
  );
}

function ReqRow({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <li className={ok ? styles.met : undefined}>
      <span className={styles.icon} aria-hidden>
        {ok ? <Check size={12} /> : <Circle size={12} />}
      </span>
      {children}
    </li>
  );
}
